import React, { useState, useEffect, useMemo } from "react";
import {
  Container, Typography, Box, Button, useTheme, useMediaQuery, 
  Pagination, Stack, Divider
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DateRangePicker from "../form/DateRangePicker";
import { format, subDays, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import { lighten } from "@material-ui/core";

import { useGetHistoryMutation } from "../../features/api/transactionApiSlice";
import { useListBetHistoryMutation } from "../../features/api/gameApiSlice";
import { useGetPromotionRecordMutation } from "../../features/api/bonusApiSlice";

const History = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));


  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [statusType, setStatusType] = useState("All");
  const [offset, setOffset] = useState(0);


  const [getTxHistory, txResult] = useGetHistoryMutation();
  const [txSubtype, setTxSubtype] = useState("deposit");
  const [getBetHistory, betResult] = useListBetHistoryMutation();
  const [getPromoHistory, promoResult] = useGetPromotionRecordMutation();

  const isLightTheme = process.env.REACT_APP_IS_LIGHT_THEME === 'true';

    const TRANSACTION_TYPES = {
    TRANSACTION: "transaction",
    BET: "bet",
    PROMOTION: "promotion"
    };

    const TIME_PRESETS = {
    TODAY: t("walletcard.promotionrecordpage.today"),
    THREE_DAYS: t("walletcard.promotionrecordpage.threedays"),
    WEEK: t("walletcard.promotionrecordpage.oneweek"),
    MONTH: t("walletcard.promotionrecordpage.onemonth"),
    //   CUSTOM: "custom"
    };

    const TX_SUBTYPE_OPTIONS = {
      deposit: t("walletcard.promotionrecordpage.deposit"),
      withdraw: t("walletcard.promotionrecordpage.withdraw")
    };
  
    const TX_STATUS_OPTIONS = {
      All: t("walletcard.promotionrecordpage.all"),
      Approved: t("walletcard.promotionrecordpage.approved"),
      Pending: t("walletcard.promotionrecordpage.pending"),
      Rejected: t("walletcard.promotionrecordpage.rejected")
    };

    const PROMO_STATUS_TYPES = {
        All: t("walletcard.promotionrecordpage.all"),
        Ongoing:  t("walletcard.promotionrecordpage.ongoing"),
        Completed: t("walletcard.promotionrecordpage.completed"),
        Rejected: t("walletcard.promotionrecordpage.rejected"),
    };

    const [mainType, setMainType] = useState(TRANSACTION_TYPES.TRANSACTION);
    const [timePreset, setTimePreset] = useState(TIME_PRESETS.WEEK);

    const activeResult = 
    mainType === TRANSACTION_TYPES.BET ? betResult : 
    mainType === TRANSACTION_TYPES.PROMOTION ? promoResult : txResult;

  const handlePresetChange = (preset) => {
    setTimePreset(preset);
    const now = new Date();
    let start = now;
    if (preset === TIME_PRESETS.TODAY) start = startOfDay(now);
    else if (preset === TIME_PRESETS.THREE_DAYS) start = subDays(now, 3);
    else if (preset === TIME_PRESETS.WEEK) start = subDays(now, 7);
    else if (preset === TIME_PRESETS.MONTH) start = subDays(now, 30);
    
    if(preset !== TIME_PRESETS.CUSTOM) {
        setStartDate(start);
        setEndDate(now);
    }
  };

const selectionButtonStyle = (isActive) => ({
  whiteSpace: 'nowrap',
  borderRadius: '8px',
  // Responsive padding: smaller on mobile
  padding: { xs: '4px 10px', sm: '6px 16px' }, 
  // Responsive font size
  fontSize: { xs: '0.75rem', sm: '0.85rem' },
  textTransform: 'none',
  transition: 'background-color 0.2s ease-in-out', 
  backgroundColor: isActive 
    ? (process.env.REACT_APP_COLOR_MOBILE_HOTGAME || theme.palette.primary.main) 
    : 'rgba(255,255,255,0.2)',
  color: (isLightTheme && isActive) 
    ? '#FFFFFF' 
    : process.env.REACT_APP_COLOR_TEXT,

    border: `1px solid ${
    isActive 
      ? process.env.REACT_APP_COLOR_HEADER_INPUTBORDER 
      : (process.env.REACT_APP_COLOR_BUTTON_FADED || 'rgba(255,255,255,0.2)')
  }`,
  
  // Prevent shrinking in the flex container
  flexShrink: 0,

  '&:hover': {
    backgroundColor: isActive 
      ? (process.env.REACT_APP_COLOR_MOBILE_HOTGAME || theme.palette.primary.main) 
      : 'rgba(255, 255, 255, 0.1)',
  }
});

const columns = useMemo(() => {
    if (mainType === TRANSACTION_TYPES.TRANSACTION) {
      return [
        { field: "id", headerName: t("walletcard.transactionpage.id"), width: 100, editable: false },
        { field: "type", headerName: t("walletcard.transactionpage.type"), width: 150, editable: false },
        { field: "amount", headerName: t("walletcard.transactionpage.amount"), width: 150, editable: false },
        { field: "status", headerName: t("walletcard.transactionpage.status"), width: 150, editable: false },
        { 
          field: "createdDate", 
          headerName: t("walletcard.transactionpage.createddate"), 
          width: 150, 
          editable: false,
          valueFormatter: (params) => format(new Date(params.value), "dd-MM-yyyy")
        },
        { field: "reason", headerName: "Reason", width: 150, editable: false },
      ];
    }

    if (mainType === TRANSACTION_TYPES.BET) {
      return [
        { field: "providerName", headerName: t("walletcard.betrecordpage.providername"), width: 150, editable: false },
        { field: "totalBetCount", headerName: t("walletcard.betrecordpage.betcount"), width: 120, editable: false },
        { field: "totalBetAmount", headerName: t("walletcard.betrecordpage.totalbetamount"), width: 150, editable: false },
        { field: "winLoseAmount", headerName: t("walletcard.betrecordpage.winlostamount"), width: 150, editable: false },
        { field: "turnoverAmount", headerName: t("walletcard.betrecordpage.turnoverAmount"), width: 150, editable: false },
      ];
    }

    if (mainType === TRANSACTION_TYPES.PROMOTION) {
      return [
        { 
          field: "createdDate", 
          headerName: t("walletcard.promotionrecordpage.date"), 
          width: 120, 
          editable: false,
          valueFormatter: (params) => format(new Date(params.value), "dd-MM-yyyy")
        },
        { field: "title", headerName: t("walletcard.promotionrecordpage.promoname"), width: 250, editable: false },
        { field: "rollover", headerName: t("walletcard.promotionrecordpage.rollovertarget"), width: 150, editable: false },
        { field: "turnover", headerName: t("walletcard.promotionrecordpage.turnovertarget"), width: 150, editable: false },
        { field: "status", headerName: t("walletcard.promotionrecordpage.status"), width: 150, editable: false },
      ];
    }
  }, [mainType, t]);

  // --- EFFECT ---
  useEffect(() => {
   const params = {
      criteria: {
        dateFrom: format(startDate, "dd-MM-yyyy"),
        dateTo: format(endDate, "dd-MM-yyyy"),
        status: statusType === "All" ? "All" : statusType, 
        type: mainType === TRANSACTION_TYPES.TRANSACTION ? txSubtype : undefined 
      },
      offset: offset,
    };

    if (mainType === TRANSACTION_TYPES.TRANSACTION) getTxHistory(params);
    else if (mainType === TRANSACTION_TYPES.BET) getBetHistory(params);
    else if (mainType === TRANSACTION_TYPES.PROMOTION) getPromoHistory(params);
  },[mainType, startDate, endDate, statusType, txSubtype, offset, getTxHistory, getBetHistory, getPromoHistory]);

const SelectionRow = ({ label, children }) => (
  <Box sx={{ 
    display: 'flex', 
    flexDirection: { xs: 'column', sm: 'row' }, // Stacked on mobile
    alignItems: { xs: 'flex-start', sm: 'center' }, 
    gap: { xs: 1, sm: 2 }, 
    mb: 2 
  }}>
    <Typography 
      variant="body2" 
      sx={{ 
        color: process.env.REACT_APP_COLOR_TEXT, 
        flexShrink: 0, 
        fontWeight: 'bold', 
        minWidth: { xs: 'unset', sm: '130px' }, // No min-width needed on mobile
        fontSize: { xs: '0.8rem', sm: '0.875rem' },
      }}
    >
      {label}
    </Typography>
    
    <Box sx={{ 
      display: 'flex', 
      gap: 1, 
      overflowX: 'auto', 
      width: '100%',
      flexWrap: 'nowrap', 
      '&::-webkit-scrollbar': { display: 'none' },
    }}>
      {children}
    </Box>
  </Box>
);
  
   return (
    <Container 
      sx={{ 
        py: { xs: 8, sm: 2 }, 
      }}
    >
      {
        !mobileView && (
          <Typography
            fontWeight="bold"
            color={process.env.REACT_APP_COLOR_TEXT || "white"}
            mb={!mobileView ? 2 : undefined}
          >
            {t("walletcard.history")}
          </Typography>
        )
      }

      <SelectionRow label={t("walletcard.promotionrecordpage.category")}>
        <Button sx={selectionButtonStyle(mainType === TRANSACTION_TYPES.TRANSACTION)} onClick={() => { setMainType(TRANSACTION_TYPES.TRANSACTION); setOffset(0); }}>{t("walletcard.promotionrecordpage.transactionrecords")}</Button>
        <Button sx={selectionButtonStyle(mainType === TRANSACTION_TYPES.BET)} onClick={() => { setMainType(TRANSACTION_TYPES.BET); setOffset(0); }}>{t("walletcard.promotionrecordpage.betrecords")}</Button>
        <Button sx={selectionButtonStyle(mainType === TRANSACTION_TYPES.PROMOTION)} onClick={() => { setMainType(TRANSACTION_TYPES.PROMOTION); setOffset(0); }}>{t("walletcard.promotionrecordpage.promotionrecords")}</Button>
      </SelectionRow>


      {mainType === TRANSACTION_TYPES.TRANSACTION && (
        <>
          <SelectionRow label={t("walletcard.promotionrecordpage.transactiontype")}>
            {Object.entries(TX_SUBTYPE_OPTIONS).map(([key, label]) => (
              <Button 
                key={key} 
                sx={selectionButtonStyle(txSubtype === key)} 
                onClick={() => { 
                  setTxSubtype(key); 
                  setOffset(0); 
                }}
              >
                {label}
              </Button>
            ))}
          </SelectionRow>
          <SelectionRow label={t("walletcard.promotionrecordpage.status")}>
            {Object.entries(TX_STATUS_OPTIONS).map(([key, label]) => (
              <Button key={key} sx={selectionButtonStyle(statusType === key)} onClick={() => { setStatusType(key); setOffset(0); }}>{label}</Button>
            ))}
          </SelectionRow>
        </>
      )}


      {mainType === TRANSACTION_TYPES.PROMOTION && (
        <SelectionRow label={t("walletcard.promotionrecordpage.type")}>
          {Object.entries(PROMO_STATUS_TYPES).map(([key, label]) => (
            <Button key={key} sx={selectionButtonStyle(statusType === key)} onClick={() => { setStatusType(key); setOffset(0); }}>{label}</Button>
          ))}
        </SelectionRow>
      )}


      <SelectionRow label={t("walletcard.promotionrecordpage.timerange")}>
        {Object.entries(TIME_PRESETS).map(([key, value]) => (
          <Button key={key} sx={selectionButtonStyle(timePreset === value)} onClick={() => handlePresetChange(value)}>{value}</Button>
        ))}
      </SelectionRow>


    <SelectionRow label={t("walletcard.promotionrecordpage.date")}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 1, 
        width: '100%',
        pt: { xs: 1.5, sm: 1 } 
      }}>
        {mobileView ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              handleStartDate={(d) => { setStartDate(d); setTimePreset(TIME_PRESETS.CUSTOM); }}
              handleEndDate={(d) => { setEndDate(d); setTimePreset(TIME_PRESETS.CUSTOM); }}
              size="small"
              isMobile={true} 
            />
          </Box>
        ) : (
          /* DESKTOP VIEW */
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              handleStartDate={(d) => { setStartDate(d); setTimePreset(TIME_PRESETS.CUSTOM); }}
              handleEndDate={(d) => { setEndDate(d); setTimePreset(TIME_PRESETS.CUSTOM); }}
              size="small"
            />
          </Box>
        )}
      </Box>
    </SelectionRow>


     {!mobileView ? (
      <Box sx={{ height: 450, width: "100%", backgroundColor: theme.palette.secondary.accent, borderRadius: '8px', overflow: 'hidden' }}>
        <DataGrid
          localeText={{
            noRowsLabel: "No transaction yet",
          }}
          rows={(activeResult?.data?.result?.content || []).map((row, index) => ({
            ...row,
            id: row.id || row.providerName || row.title || index,
          }))}
          columns={columns}
          hideFooter
          sx={{
            '.MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '&.MuiDataGrid-root': {
              border: 'none',
            },

            color: "white",
            backgroundColor: theme.palette.secondary.accent,

            '& .MuiDataGrid-overlay': {
              backgroundColor: 'transparent',
            },

            '& .MuiDataGrid-columnHeaders': {
              color: "white",
              borderBottom: '1px solid rgba(255,255,255,0.1)',
            },

            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'center',
            },

            '& .MuiDataGrid-cell': {
              color: "white",
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
            },

            "& .MuiDialogContent-root": {
              overflowY: "auto",
              "::-webkit-scrollbar": {
                width: "12px",
              },
              "::-webkit-scrollbar-track": {
                background: theme.palette.text.disabled,
              },
              "::-webkit-scrollbar-thumb": {
                background: theme.palette.background.paper,
              },
            },
          }}
        />
      </Box>
) : (
  <Box sx={{ height: 400, overflowY: 'auto', p: 1 }}>
    {activeResult?.data?.result?.content?.length > 0 ? (
      activeResult.data.result.content.map((item, idx) => (
        <Box 
          key={idx} 
          sx={{ 
            p: 1.5, 
            mb: 1.5, 
            color: process.env.REACT_APP_COLOR_TEXT,
            backgroundColor: 'rgba(255,255,255,0.05)', 
            borderRadius: '8px', 
            border: `1px solid ${process.env.REACT_APP_COLOR_BUTTON_FADED || 'rgba(255,255,255,0.1)'}`
          }}
        >
          <Typography fontSize={11} sx={{ color: process.env.REACT_APP_COLOR_TEXT, opacity: 0.5, mb: 0.5 }}>
            {item.id ? `ID: ${item.id}` : (item.providerName || "")}
          </Typography>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
            <Typography fontWeight="bold" sx={{ color: process.env.REACT_APP_COLOR_TEXT, fontSize: '0.9rem' }}>
              {mainType === TRANSACTION_TYPES.TRANSACTION && item.type}
              {mainType === TRANSACTION_TYPES.BET && item.providerName}
              {mainType === TRANSACTION_TYPES.PROMOTION && item.title}
            </Typography>

            <Typography sx={{ fontWeight: 'bold', color: item.winLoseAmount < 0 ? "error.main" : "success.main" }}>
              {item.amount || item.totalBetAmount || item.turnover}
            </Typography>
          </Box>

          <Typography fontSize={11} sx={{ color: process.env.REACT_APP_COLOR_TEXT, opacity: 0.7, mt: 1 }}>
            {item.createdDate ? format(new Date(item.createdDate), "yyyy-MM-dd HH:mm") : ""}
          </Typography>
          
        </Box>
      ))
    ) : (
      <Typography sx={{ color: process.env.REACT_APP_COLOR_TEXT, textAlign: 'center', mt: 10 }}>
        {activeResult.isLoading ? "Loading..." : "No Records Found"}
      </Typography>
    )}
  </Box>
)}
      <Stack mt={3} alignItems="center">
        <Pagination
          count={activeResult?.data?.result?.totalPages || 1}
          page={offset + 1}
          onChange={(e, p) => setOffset(p - 1)}
          shape="rounded"
          sx={{
            "& .MuiPaginationItem-root": {
             color: process.env.REACT_APP_COLOR_PAGINATION_NUMBER2 || "white",
             "&.Mui-selected": {
             background: lighten(
                process.env.REACT_APP_COLOR_PAGINATION || theme.palette.background.paper,
                   0.02
              ),
                color: process.env.REACT_APP_COLOR_PAGINATION_NUMBER1 || "white",
              },
              },
              "& .MuiPaginationItem-icon": {
                  color: process.env.REACT_APP_COLOR_PAGINATION_NUMBER2 || "white",
              },
        }}
        />
      </Stack>
    </Container>
  );
};

export default History;